// src/components/SidebarLayout.jsx
//
// Mobile + desktop layout shell.
//
// Mobile chrome:
//   - Top header: pitch bar with Scrimly wordmark + role pill (left),
//     notification bell + settings cog (right).
//   - Bottom: BottomTabBar (primary nav). The "More" tab opens the
//     hamburger drawer for less-frequent nav (settings/profile/etc).
//
// Desktop chrome: traditional fixed sidebar (left); collapses to icon-only
// on tablet. Sidebar component is injected via the `sidebar` prop so this
// layout file stays UI-only.
'use client'

import * as Headless from '@headlessui/react'
import React, { useRef, useState, useEffect, cloneElement, isValidElement, useMemo } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { useAuth } from '@/context/AuthContext'
import { useTeam } from '@/context/TeamContext'
import { useRole } from '@/context/RoleContext'
import useInboxUnreadCount from '@/hooks/useInboxUnreadCount'
import useEntitlements from '@/hooks/useEntitlements'
import useHasPaidSeasons from '@/hooks/useHasPaidSeasons'
import useLogout from '@/hooks/useLogout'
import { setAppLanguage } from '@/i18n'

import {
  Cog8ToothIcon,
  XMarkIcon,
  QuestionMarkCircleIcon,
  ChartBarIcon,
  BuildingOffice2Icon,
  RectangleGroupIcon,
  MegaphoneIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  SparklesIcon,
  LightBulbIcon,
  ArrowRightStartOnRectangleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'

import { Avatar } from '@/components/avatar'
import NotificationDropdown from '@/components/NotificationDropdown'
import BottomTabBar from '@/components/Layout/BottomTabBar'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

/* ----------------------------------------------------
   Mobile More Menu

   The mobile "More" drawer used to stuff the entire desktop sidebar
   (12+ items, multiple section headers, footer dropdown) into a 320px
   panel — a wall of items that didn't respect the bottom tab bar
   already covering Home / Schedule / Messages.

   This component is the mobile-specific alternative: only items NOT
   already in the BottomTabBar, with role-aware gating that mirrors
   the desktop sidebar (hasCoachTeams, isClub, userProfile.clubId,
   isParent). Each row is a 44px+ tap target. Sign out is bottom-
   pinned and tinted red as a destructive action.
---------------------------------------------------- */
function MobileLanguageToggle() {
  const { i18n } = useTranslation()
  const current = String(i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en'

  return (
    <div
      className="inline-flex items-center gap-1 rounded-full bg-white/10 p-1 text-[12px] font-semibold"
      role="group"
      aria-label="Language"
    >
      {['en', 'es'].map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setAppLanguage(code)}
          aria-pressed={current === code}
          className={clsx(
            'min-w-[40px] px-3 py-1 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d]/60',
            current === code
              ? 'bg-[#b5f03d] text-[#0d1f14] shadow-sm'
              : 'text-white/70 hover:bg-white/10 hover:text-white'
          )}
        >
          {code === 'en' ? 'EN' : 'ES'}
        </button>
      ))}
    </div>
  )
}

function MoreRow({ to, label, icon: Icon, onClick, external = false, danger = false }) {
  const cls = clsx(
    'group flex w-full items-center gap-3 rounded-xl px-3 text-left transition-colors',
    'min-h-[48px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d]/60',
    danger
      ? 'text-red-300 hover:bg-red-500/10 hover:text-red-200'
      : 'text-white/85 hover:bg-white/[0.07] hover:text-white'
  )

  const content = (
    <>
      <Icon
        className={clsx(
          'h-5 w-5 flex-shrink-0',
          danger ? 'text-red-300' : 'text-white/55 group-hover:text-[#b5f03d]'
        )}
      />
      <span className="flex-1 text-[15px] font-medium truncate">{label}</span>
      {!danger && (
        <ChevronRightIcon className="h-4 w-4 text-white/30 group-hover:text-white/60" aria-hidden />
      )}
    </>
  )

  if (onClick && !to) {
    return (
      <button type="button" onClick={onClick} className={cls}>
        {content}
      </button>
    )
  }

  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" onClick={onClick} className={cls}>
        {content}
      </a>
    )
  }

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        clsx(cls, isActive && !danger ? 'bg-white/[0.06] text-white' : '')
      }
    >
      {content}
    </NavLink>
  )
}

function MoreSectionHeader({ children }) {
  return (
    <div
      className="px-3 pt-4 pb-1.5 text-[11px] font-bold uppercase tracking-[1.2px] text-white/45"
      style={{ fontFamily: HEAD_FONT }}
    >
      {children}
    </div>
  )
}

function MobileMoreMenu({ onClose }) {
  const { t } = useTranslation()
  const { userProfile, firebaseUser, isAuthenticated } = useAuth()
  const { isParent, hasCoachTeams } = useRole()
  const { isPro, isFounder, isClub } = useEntitlements(firebaseUser?.uid || null)
  const hasPaidSeasons = useHasPaidSeasons(firebaseUser?.uid || null)
  const doLogout = useLogout()

  const handleLogout = async () => {
    await doLogout()
    onClose?.()
  }

  const close = () => onClose?.()

  const clubId = userProfile?.clubId
  const safeClubId = clubId ? encodeURIComponent(clubId) : null
  const showClubSection = hasCoachTeams && isClub && !!clubId
  // Finances only for coaches with at least one PAID season — matches
  // the desktop sidebar gate (2026-07-24 sidebar audit).
  const showFinances = hasCoachTeams && hasPaidSeasons

  const feedbackHref = `mailto:hello@getscrimly.com?subject=${encodeURIComponent(
    'Scrimly feedback'
  )}&body=${encodeURIComponent(
    `\n\n— Sent from ${typeof window !== 'undefined' ? window.location.href : 'Scrimly'}`
  )}`

  return (
    <div
      className="flex h-full flex-col text-white"
      style={{
        backgroundColor: '#0a1810',
        backgroundImage: `
          radial-gradient(60% 60% at 10% -10%, rgba(181,240,61,0.05) 0%, rgba(181,240,61,0) 60%),
          linear-gradient(180deg, #0d1f14 0%, #0a1810 100%)
        `,
        fontFamily: "'Barlow', sans-serif",
      }}
    >
      {/* Identity header — sticky top, mirrors the desktop sidebar
          footer block (avatar + name + role/tier pill). On mobile it
          belongs at the TOP of the drawer because the user's identity
          is the first thing they look at when they open "More". */}
      {isAuthenticated && userProfile ? (
        <div
          className="sticky top-0 z-10 px-4 pt-5 pb-4 border-b border-white/10"
          style={{
            paddingTop: 'max(1.25rem, env(safe-area-inset-top))',
            backgroundColor: 'rgba(13,31,20,0.95)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="flex items-center gap-3">
            <div className="relative flex-shrink-0">
              {(isPro || isFounder) && (
                <span
                  aria-hidden="true"
                  className="absolute -inset-0.5 z-10 rounded-full"
                  style={{ background: 'linear-gradient(45deg, #b5f03d, #94cc28)' }}
                />
              )}
              <Avatar
                src={userProfile.photoURL || firebaseUser?.photoURL || '/default-avatar.png'}
                className="relative z-20 size-12 rounded-full"
                alt={userProfile.displayName || 'User'}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[16px] font-bold text-white leading-tight">
                {userProfile.displayName || firebaseUser?.displayName || t('nav.account')}
              </div>
              <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                <span
                  className={clsx(
                    'text-[10px] font-bold tracking-[1px] uppercase px-1.5 py-0.5 rounded border leading-none',
                    isParent
                      ? 'bg-sky-400/15 text-sky-300 border-sky-400/30'
                      : 'bg-white/[0.06] text-white/70 border-white/15'
                  )}
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {isParent ? 'Parent' : 'Coach'}
                </span>
                {isFounder ? (
                  <span
                    className="bg-[#b5f03d]/15 text-[#b5f03d] text-[10px] font-bold px-1.5 py-0.5 rounded border border-[#b5f03d]/30 tracking-[1px] uppercase leading-none"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    FOUNDER
                  </span>
                ) : isPro ? (
                  <span
                    className="bg-[#b5f03d]/15 text-[#b5f03d] text-[10px] font-bold px-1.5 py-0.5 rounded border border-[#b5f03d]/30 tracking-[1px] uppercase leading-none"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    PRO
                  </span>
                ) : firebaseUser?.email ? (
                  <span className="truncate text-[11px] text-white/55 leading-none">
                    {firebaseUser.email}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Body — scrollable list of less-frequent nav items. Bottom tab
          (Home / Schedule / Messages / Chat) is intentionally NOT
          duplicated here. */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 space-y-1">
        {/* Profile is the avatar block in the sticky header above —
            no separate row needed. Settings is the umbrella for
            profile + notifications + language. */}
        <MoreRow to="/settings" label={t('nav.settings')} icon={Cog8ToothIcon} onClick={close} />
        <MoreRow to="/support" label={t('nav.helpSupport')} icon={QuestionMarkCircleIcon} onClick={close} />
        {showFinances && (
          <MoreRow
            to="/reports/financial"
            label={t('nav.finances')}
            icon={ChartBarIcon}
            onClick={close}
          />
        )}

        {showClubSection && (
          <>
            <MoreSectionHeader>{t('nav.club')}</MoreSectionHeader>
            <MoreRow
              to={`/clubs/${safeClubId}`}
              label={t('nav.clubOverview')}
              icon={BuildingOffice2Icon}
              onClick={close}
            />
            <MoreRow
              to={`/clubs/${safeClubId}/programs`}
              label={t('nav.programs')}
              icon={RectangleGroupIcon}
              onClick={close}
            />
            <MoreRow
              to={`/clubs/${safeClubId}/broadcast`}
              label={t('nav.broadcast')}
              icon={MegaphoneIcon}
              onClick={close}
            />
            <MoreRow
              to={`/clubs/${safeClubId}/members`}
              label={t('nav.members')}
              icon={UserGroupIcon}
              onClick={close}
            />
            <MoreRow
              to={`/clubs/${safeClubId}/settings`}
              label={t('nav.clubSettings')}
              icon={Cog6ToothIcon}
              onClick={close}
            />
          </>
        )}

        {/* Language pill — chrome control, not a nav row, so render
            it as a labelled section with the toggle inline. */}
        <div className="px-3 pt-5 pb-2 flex items-center justify-between">
          <span
            className="text-[11px] font-bold uppercase tracking-[1.2px] text-white/45"
            style={{ fontFamily: HEAD_FONT }}
          >
            {t('settings.sections.language')}
          </span>
          <MobileLanguageToggle />
        </div>

        <MoreRow to="/changelog" label={t('nav.whatsNew')} icon={SparklesIcon} onClick={close} />
        <MoreRow
          to={feedbackHref}
          label={t('nav.shareFeedback')}
          icon={LightBulbIcon}
          external
          onClick={close}
        />
      </div>

      {/* Footer — destructive sign out, separated visually so users
          don't tap it by accident on the way to /settings. */}
      {isAuthenticated && (
        <div
          className="border-t border-white/10 px-3 py-3"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <MoreRow
            label={t('nav.signOut')}
            icon={ArrowRightStartOnRectangleIcon}
            onClick={handleLogout}
            danger
          />
        </div>
      )}
    </div>
  )
}

/* ----------------------------------------------------
   Mobile Sidebar Wrapper

   Slides in from the left when the BottomTabBar's "More" tab is tapped.
   Hosts the mobile-specific MobileMoreMenu (NOT the desktop sidebar —
   the desktop sidebar is way too much for a 320px drawer).
---------------------------------------------------- */
function MobileSidebar({ open, onClose, children }) {
  const panelRef = useRef(null)
  const startX = useRef(0)
  const deltaX = useRef(0)
  const dragging = useRef(false)

  useEffect(() => {
    // Only attach swipe-to-close listeners while the drawer is visible.
    // Prevents global mousemove/mouseup leaks on desktop/closed state.
    if (!open) return
    const el = panelRef.current
    if (!el) return

    const onStart = (e) => {
      dragging.current = true
      startX.current = 'touches' in e ? e.touches[0].clientX : e.clientX
      deltaX.current = 0
    }

    const onMove = (e) => {
      if (!dragging.current) return
      const x = 'touches' in e ? e.touches[0].clientX : e.clientX
      deltaX.current = Math.min(0, x - startX.current)
      el.style.transform = `translateX(${deltaX.current}px)`
    }

    const onEnd = () => {
      if (!dragging.current) return
      dragging.current = false
      const shouldClose = Math.abs(deltaX.current) > 90
      el.style.transform = ''
      if (shouldClose) onClose()
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: true })
    el.addEventListener('touchend', onEnd)
    el.addEventListener('mousedown', onStart)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onEnd)

    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('mousedown', onStart)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onEnd)
    }
  }, [open, onClose])

  return (
    <Headless.Dialog open={open} onClose={onClose} className="relative z-50 md:hidden">
      <Headless.Transition
        appear
        show={open}
        enter="transition-opacity duration-300 ease-out"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-200 ease-in"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden />
      </Headless.Transition>

      <div className="fixed inset-0 flex">
        <Headless.DialogPanel
          ref={panelRef}
          className="
            fixed inset-y-0 left-0 z-50 w-[85vw] max-w-xs
            transform transition duration-300 ease-in-out
            data-closed:-translate-x-full
            flex flex-col shadow-2xl
          "
        >
          <div className="relative flex-1 overflow-hidden rounded-r-2xl ring-1 ring-white/10 bg-[#1a2238]">
            <div className="absolute top-4 right-4 z-50">
              <Headless.CloseButton
                as="button"
                aria-label="Close navigation"
                className="
                  rounded-full p-2 text-white/60 hover:text-white hover:bg-white/10 
                  transition-colors focus:outline-none focus:ring-2 focus:ring-white/30
                "
              >
                <XMarkIcon className="h-6 w-6" />
              </Headless.CloseButton>
            </div>
            <div className="h-full w-full">
              {children}
            </div>
          </div>
        </Headless.DialogPanel>
      </div>
    </Headless.Dialog>
  )
}

const STORAGE_KEY = 'scrimly.sidebar.collapsed'

function SidebarLayout({ sidebar, navbar, children }) {
  const [showSidebar, setShowSidebar] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)
  const { i18n } = useTranslation()
  const esUi = String(i18n?.language || 'en').toLowerCase().startsWith('es')

  // 1. Setup Notification Monitoring for Mobile Header
  const { firebaseUser, userProfile } = useAuth()
  const { currentTeam } = useTeam()
  const scope = currentTeam ? 'team' : 'user'
  const targetId = currentTeam ? currentTeam.id : (firebaseUser?.uid || null)

  const { count: chatUnreadCount, increased: inboxIncreased } = useInboxUnreadCount(
    targetId,
    { scope }
  )

  const showMessagesPing = chatUnreadCount > 0
  const pulseMessages = showMessagesPing && inboxIncreased

  // Role pill in the mobile header — mirrors the one in the desktop
  // sidebar footer so users always know which role they're signed in as
  // (mobile users never see the desktop sidebar).
  const isParent = String(userProfile?.role || '').toLowerCase() === 'parent'

  useEffect(() => {
    if (typeof window === 'undefined') return
    const tabletMql = window.matchMedia('(min-width: 768px) and (max-width: 1023.98px)')
    const desktopMql = window.matchMedia('(min-width: 1024px)')

    const apply = () => {
      setIsTablet(Boolean(tabletMql.matches))
      setIsDesktop(Boolean(desktopMql.matches))
    }

    apply()
    tabletMql.addEventListener?.('change', apply)
    desktopMql.addEventListener?.('change', apply)
    return () => {
      tabletMql.removeEventListener?.('change', apply)
      desktopMql.removeEventListener?.('change', apply)
    }
  }, [])

  const [collapsed, setCollapsed] = useState(false)

  useEffect(() => {
    if (!isDesktop && !isTablet) return
    if (isTablet) {
      setCollapsed(true)
      return
    }
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      setCollapsed(saved === '1')
    } catch {
      setCollapsed(false)
    }
  }, [isDesktop, isTablet])

  const toggleCollapsed = () => {
    const next = !collapsed
    setCollapsed(next)
    if (isDesktop) {
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
      } catch {}
    }
  }

  const sidebarWidthClass = collapsed ? 'w-20' : 'w-64'
  const leftOffsetClass = collapsed ? 'md:pl-20' : 'md:pl-64'
  const hasFixedSidebar = isTablet || isDesktop

  const desktopSidebar = useMemo(() => {
    if (!isValidElement(sidebar)) return sidebar
    return cloneElement(sidebar, {
      onItemClick: undefined,
      collapsed,
      onToggleCollapsed: toggleCollapsed,
      showCollapseToggle: isDesktop || isTablet,
    })
  }, [sidebar, collapsed, isDesktop, isTablet])

  // The mobile drawer is no longer a clone of the desktop sidebar —
  // it now renders MobileMoreMenu, a mobile-specific list curated to
  // complement the BottomTabBar (see MobileMoreMenu above).

  return (
    <div className="relative isolate flex min-h-svh w-full max-lg:flex-col bg-[#f7faf5]">
      {/* Skip-to-content link for keyboard and screen-reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-zinc-900 focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#b5f03d]"
      >
        Skip to main content
      </a>

      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-50 max-md:hidden transition-[width] duration-200', 
          sidebarWidthClass
        )}
      >
        {desktopSidebar}
      </div>

      <MobileSidebar open={showSidebar} onClose={() => setShowSidebar(false)}>
        <MobileMoreMenu onClose={() => setShowSidebar(false)} />
      </MobileSidebar>

      {/* --- MOBILE HEADER ---
          Slim pitch bar matching the bottom-tab nav. The hamburger +
          avatar moved to the BottomTabBar's "More" tab. Logo on the
          left (brand presence), notification bell on the right (still
          reachable from anywhere). Uses safe-area-inset-top for the
          iOS notch / Android camera cutout. */}
      <header
        className="sticky top-0 z-50 flex items-center justify-between px-4 md:hidden bg-[#0d1f14] border-b border-white/10"
        style={{
          paddingTop: 'max(0.5rem, env(safe-area-inset-top))',
          paddingBottom: '0.5rem',
        }}
      >
        {/* Logo + role pill — left-aligned. Pill mirrors the one in
            the desktop sidebar footer so mobile users get the same
            role-at-a-glance signal. */}
        <div className="flex items-center gap-2">
          <Link
            to="/dashboard"
            className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d]/60 rounded-md"
            aria-label="Scrimly home"
          >
            <span
              className="text-[#b5f03d] leading-none"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 900,
                fontSize: 22,
                letterSpacing: '-0.5px',
              }}
            >
              Scrim<span className="text-white">ly</span>
            </span>
          </Link>
          {userProfile && (
            <span
              className={clsx(
                'text-[9px] font-bold tracking-[1px] uppercase px-1.5 py-0.5 rounded border leading-none',
                isParent
                  ? 'bg-sky-400/15 text-sky-300 border-sky-400/30'
                  : 'bg-white/[0.06] text-white/70 border-white/15',
              )}
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {isParent ? (esUi ? 'Padre' : 'Parent') : (esUi ? 'Entrenador' : 'Coach')}
            </span>
          )}
        </div>

        {/* Right: Notification bell + Settings shortcut. Avatar +
            hamburger moved to the BottomTabBar's "More" tab to reduce
            top-bar clutter. Settings is a one-tap to /settings (profile
            tab is the default — settings sub-nav has Profile and
            Notifications, role-aware). */}
        <div className="flex items-center gap-1">
          <NotificationDropdown tone="dark" />
          <Link
            to="/settings"
            aria-label="Settings"
            className="inline-flex items-center justify-center rounded-lg p-2 text-white/80 hover:text-white hover:bg-white/10 active:scale-[0.92] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d]/60"
          >
            <Cog8ToothIcon className="h-6 w-6" />
          </Link>
        </div>
      </header>

      {navbar ? <div className="max-md:hidden">{navbar}</div> : null}

      <main
        id="main-content"
        tabIndex={-1}
        className={clsx(
          'flex flex-1 flex-col focus:outline-none',
          'md:min-w-0',
          hasFixedSidebar && leftOffsetClass,
          // Reserve space at the bottom on mobile so content isn't hidden
          // beneath the BottomTabBar. Tab bar is ~64px + safe-area-inset.
          'pb-[calc(64px+env(safe-area-inset-bottom))] md:pb-0',
        )}
      >
        <div className="grow p-4 sm:p-6 md:p-0 bg-[#f7faf5]">
          <div className="mx-auto">{children}</div>
        </div>
      </main>

      {/* Mobile-only bottom tab bar — primary nav surface on phones.
          Hamburger drawer becomes the "More" tab for less-common nav. */}
      <BottomTabBar
        onOpenMore={() => setShowSidebar(true)}
        drawerOpen={showSidebar}
      />
    </div>
  )
}

export { SidebarLayout }